/**
 * Phép tính thuần của mẫu văn bản, chuyển nguyên từ `data/types.ts` của foxainative: dựng `fields` từ
 * ô nhập, sinh `key` snake_case không dấu, đổi qua lại thể thức ↔ `default_meta`, thay placeholder.
 * Không chạm DOM, không gọi HTTP.
 */
import type { KieuTruong, TheThucMau, TruongMau } from '../types.ts';

/** Trường ở tầng giao diện; `key` của backend sinh từ nhãn khi lưu. */
export interface TruongNhap {
  /** Nhãn hiển thị người dùng gõ. */
  name: string;
  type: KieuTruong;
  /** Gợi ý cho AI (thành `hint`). */
  note: string;
  required: boolean;
  /** AI tự điền từ văn bản nguồn (mặc định bật). */
  aiFill: boolean;
  /** Lựa chọn, chỉ dùng khi `type = SELECT`. */
  options: string[];
}

export const TRUONG_RONG: TruongNhap = {
  name: '',
  type: 'TEXT',
  note: '',
  required: false,
  aiFill: true,
  options: [],
};

/** Thể thức ở tầng giao diện; `nn` ⇄ `noinhan`, `headerCenter` ⇄ `header_style = CENTERED`. */
export interface TheThucNhap {
  cqch: string;
  cq: string;
  so: string;
  kyhieu: string;
  dd: string;
  cv: string;
  ky: string;
  nn: string;
  headerCenter: boolean;
}

export const THE_THUC_RONG: TheThucNhap = {
  cqch: '',
  cq: '',
  so: '',
  kyhieu: '',
  dd: '',
  cv: '',
  ky: '',
  nn: '',
  headerCenter: false,
};

/** Key snake_case không dấu, hợp lệ với backend (`^[a-z][a-z0-9_]*$`). */
export function sinhKhoa(nhan: string): string {
  const goc = nhan
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!goc) return 'field';
  return /^[a-z]/.test(goc) ? goc : `f_${goc}`;
}

/** `fields` gửi backend và bảng nhãn → key (đã khử trùng) để đổi chip thành `{{key}}`. */
export function dungTruong(ds: TruongNhap[]): {
  fields: TruongMau[];
  khoaTheoNhan: Record<string, string>;
} {
  const daDung = new Set<string>();
  const khoaTheoNhan: Record<string, string> = {};
  const fields = ds
    .filter((f) => f.name.trim())
    .map((f): TruongMau => {
      const label = f.name.trim();
      const goc = sinhKhoa(label);
      let key = goc;
      let n = 2;
      while (daDung.has(key)) key = `${goc}_${n++}`;
      daDung.add(key);
      khoaTheoNhan[label] = key;
      return {
        key,
        label,
        type: f.type,
        hint: f.note.trim() || undefined,
        required: f.required,
        ai_fill: f.aiFill,
        options: f.type === 'SELECT' ? f.options.map((o) => o.trim()).filter(Boolean) : undefined,
      };
    });
  return { fields, khoaTheoNhan };
}

export function tuTruongMau(t: TruongMau): TruongNhap {
  return {
    name: t.label,
    type: t.type ?? 'TEXT',
    note: t.hint ?? '',
    required: !!t.required,
    aiFill: t.ai_fill ?? true,
    options: t.options ?? [],
  };
}

export function sangTheThucMau(f: TheThucNhap): TheThucMau {
  const gon = (s: string) => s.trim() || undefined;
  return {
    cqch: gon(f.cqch),
    cq: gon(f.cq),
    so: gon(f.so),
    kyhieu: gon(f.kyhieu),
    dd: gon(f.dd),
    cv: gon(f.cv),
    ky: gon(f.ky),
    noinhan: gon(f.nn),
    header_style: f.headerCenter ? 'CENTERED' : undefined,
  };
}

export function tuTheThucMau(m: TheThucMau | null | undefined): TheThucNhap {
  return {
    cqch: m?.cqch ?? '',
    cq: m?.cq ?? '',
    so: m?.so ?? '',
    kyhieu: m?.kyhieu ?? '',
    dd: m?.dd ?? '',
    cv: m?.cv ?? '',
    ky: m?.ky ?? '',
    nn: m?.noinhan ?? '',
    headerCenter: m?.header_style === 'CENTERED',
  };
}

const PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/g;

/** Thoát HTML cho chữ đặt vào khung giấy. */
export function thoatHtml(s: string): string {
  return (s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** `{{key}}` → `[Nhãn]` đã thoát HTML, như `_preview_from_body` của backend. */
export function thayBangNhan(than: string, fields: TruongMau[] | null | undefined): string {
  const nhan: Record<string, string> = {};
  (fields ?? []).forEach((f) => {
    nhan[f.key] = f.label;
  });
  return than.replace(PLACEHOLDER, (_m, k: string) => thoatHtml(`[${nhan[k] ?? k}]`));
}

/** Mọi key trong `{{…}}` của thân, theo thứ tự xuất hiện, không trùng. */
export function khoaTrongThan(than: string): string[] {
  const ra: string[] = [];
  for (const m of than.matchAll(PLACEHOLDER)) if (!ra.includes(m[1]!)) ra.push(m[1]!);
  return ra;
}
